import React, { useState } from 'react';
import { Box, Typography, FormGroup, FormControlLabel, Checkbox, Chip, IconButton, Tooltip, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { Info as InfoIcon, Close as CloseIcon } from '@mui/icons-material';
import DialogWrapper from './DialogWrapper';

export interface PermissionItem {
  id: string;
  name: string;
  is_active?: boolean;
  is_implicit?: boolean;
  description?: string;
}

interface PermissionsListProps {
  title: string;
  available: PermissionItem[]; // already assigned (accessible)
  allItems: PermissionItem[]; // all possible items (requestable)
  pendingIds?: string[]; // requested and pending approval
  requestedIds: string[];
  onToggleRequest: (id: string) => void;
  compact?: boolean;
}

const PermissionsList: React.FC<PermissionsListProps> = ({
  title,
  available,
  allItems,
  pendingIds = [],
  requestedIds,
  onToggleRequest,
  compact
}) => {
  const nonAccessible = allItems.filter(a => !available.some(av => av.id === a.id) && !a.is_implicit);
  const pendingIdSet = new Set(pendingIds);
  const pendingNonAccessible = nonAccessible.filter(item => pendingIdSet.has(item.id));
  const requestableNonAccessible = nonAccessible.filter(item => !pendingIdSet.has(item.id));
  
  // State for description dialog
  const [descriptionDialogOpen, setDescriptionDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<PermissionItem | null>(null);

  const handleOpenDescription = (item: PermissionItem, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent checkbox toggle
    setSelectedItem(item);
    setDescriptionDialogOpen(true);
  };

  const handleCloseDescription = () => {
    setDescriptionDialogOpen(false);
    setSelectedItem(null);
  };

  // Helper to render item label with optional info button
  const renderItemLabel = (item: PermissionItem, showInactiveChip?: boolean, showPendingChip?: boolean) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Typography>{item.name}</Typography>
      {item.description && (
        <Tooltip title="Click to read description" arrow>
          <IconButton
            size="small"
            onClick={(e) => handleOpenDescription(item, e)}
            sx={{ 
              p: 0.25,
              ml: 0.5,
              color: 'primary.main',
              '&:hover': { color: 'text.secondary' }
            }}
          >
            <InfoIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      )}
      {showPendingChip && (
        <Chip label="Request sent - approval pending" size="small" color="info" sx={{ height: 20, fontSize: '0.7rem' }} />
      )}
      {showInactiveChip && !item.is_active && (
        <Chip label="Inactive" size="small" color="warning" sx={{ height: 20, fontSize: '0.7rem' }} />
      )}
    </Box>
  );

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0 }}>{title}</Typography>
      <FormGroup>
        {available.map(item => (
          <FormControlLabel
            key={item.id}
            control={<Checkbox checked disabled />}
            label={renderItemLabel(item, true)}
            sx={{ my: compact ? -0.9 : 0 }}
          />
        ))}

        {pendingNonAccessible.map(item => (
          <FormControlLabel
            key={item.id}
            control={<Checkbox checked disabled />}
            label={renderItemLabel(item, false, true)}
            sx={{ my: compact ? -0.9 : 0 }}
          />
        ))}

        {requestableNonAccessible.map(item => (
          <FormControlLabel
            key={item.id}
            control={
              <Checkbox
                checked={requestedIds.includes(item.id)}
                onChange={() => onToggleRequest(item.id)}
              />
            }
            label={renderItemLabel(item)}
            sx={{ my: compact ? -0.9 : 0 }}
          />
        ))}

        {available.length === 0 && nonAccessible.length === 0 && (
          <Typography variant="body2" color="text.secondary">No permissions available.</Typography>
        )}
      </FormGroup>

      {/* Description Dialog */}
      <DialogWrapper
        open={descriptionDialogOpen}
        onClose={handleCloseDescription}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedItem?.name || 'Item'}
          <IconButton
            aria-label="close"
            onClick={handleCloseDescription}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, mt: 1 }}>
            {selectedItem?.description || 'No description available.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDescription} color="primary" variant="contained">
            Close
          </Button>
        </DialogActions>
      </DialogWrapper>
    </Box>
  );
};

export default PermissionsList;
